import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  GetIssuesRequestDTO,
  IssueColumnId,
  IssueDTO,
  IssueSource,
  IssueStatus,
  MODULE,
  PDF_SCOPE,
  PdfScope,
  RecordState,
  UpdateIssuesRequestDTO,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useInView } from 'react-intersection-observer';
import { useLocation } from 'react-router-dom';

import { ArchiveConfirmationDialog } from '@/components/common/archive-confirmation-dialog.tsx';
import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog.tsx';
import { RestoreConfirmationDialog } from '@/components/common/restore-confirmation-dialog.tsx';
import { ShareDialog } from '@/components/common/share-dialog';
import { FeedbackPrompt } from '@/components/feedback/feedback-prompt';
import { ArchiveIcon } from '@/components/icons/archive.tsx';
import { DeleteIcon } from '@/components/icons/delete.tsx';
import DownloadCsvIcon from '@/components/icons/download-csv';
import { FolderOpenIcon } from '@/components/icons/folder-open.tsx';
import GearIcon from '@/components/icons/gear';
import LinkViewIcon from '@/components/icons/link-view';
import TagIcon from '@/components/icons/tag';
import UndoIcon from '@/components/icons/undo.tsx';
import IssueDrawer from '@/components/issues/issue-drawer';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { useTopbarStore } from '@/components/topbar/topbar-store';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle.tsx';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { useFeedbackPrompt } from '@/hooks/use-feedback-prompt';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import ShareIcon from '@/legacy/components/icons/share';
import { tagKeys } from '@/queries/assets';
import { buildFilterFields } from '@/queries/column-filter';
import { dashboardKeys } from '@/queries/dashboard';
import { getIssuesInfiniteQueryOptions, issueKeys } from '@/queries/issues';
import { repairKeys } from '@/queries/repairs';
import { getUnitsQueryOptions } from '@/queries/units';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, overLimitExportError, RegisterExportQuery } from '@/utils/register-pdf-export';
import { getShareTargets } from '@/utils/share-targets';

import { createIssueTableColumns, FILTER_FIELD_MAP, groupOptions } from './table-meta.tsx';
import { IssuesTable, NoIssueAvailableDisplay, NoIssueFoundFromFiltersDisplay, NoIssueFoundFromSearchDisplay } from './table.tsx';
import { TopbarActions } from './topbar-actions.tsx';
import { UpdateIssuesStatusDialog } from './update-issue-status-dialog.tsx';
import { UpdateIssuesTagsDialog } from './update-issue-tags-dialog.tsx';

const IssueActivityPage = () => {
  const queryClient = useQueryClient();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const { ref: loadMoreRef, inView: loadMoreInView } = useInView({ root: scrollRoot, rootMargin: '200px' });
  const toast = useToast();
  const { state } = useLocation();
  const { setModuleFilters } = useModulesStore();
  const { isAdmin, canExportCsv } = usePermissions();

  const { triggerForIssue, promptProps: feedbackPromptProps } = useFeedbackPrompt('issues');

  const { data: units, isLoading: isLoadingUnits } = useQuery(getUnitsQueryOptions());

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.Issues, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns: createIssueTableColumns({ units }),
  });

  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [openArchiveConfirmation, setOpenArchiveConfirmation] = useState(false);
  const [openRestoreConfirmation, setOpenRestoreConfirmation] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [openIssuesStatusUpdate, setOpenIssuesStatusUpdate] = useState(false);
  const [openIssuesTagsUpdate, setOpenIssuesTagsUpdate] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [issueState, setIssueState] = useState<RecordState>(RecordState.Active);
  const [search, setSearch] = useState('');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<IssueDTO | undefined>(undefined);
  const [isIssueDrawerOpen, setIsIssueDrawerOpen] = useState(!!state?.issueId);

  const debouncedSearch = useDebounce(search, 500);

  useLayoutEffect(() => {
    useTopbarStore.getState().setActions(<TopbarActions />);

    return () => {
      useTopbarStore.getState().setActions(<></>);
    };
  }, []);

  const {
    data: issuesData,
    fetchNextPage: fetchNextIssuesPage,
    hasNextPage: hasNextIssuesPage,
    isFetching: isFetchingIssues,
    isFetchingNextPage: isFetchingNextIssuesPage,
    error: issuesError,
  } = useInfiniteQuery(
    getIssuesInfiniteQueryOptions({
      filters: {
        state: issueState,
        search: debouncedSearch,
        ...buildFilterFields(filters, FILTER_FIELD_MAP, {
          transformValue: (id, raw) =>
            id === 'number' || id === 'workOrderNumber' || id === 'repairNumber' ? (raw as string | undefined)?.replace(/[^0-9]/g, '') : raw,
        }),
      },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetIssuesRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetIssuesRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedIssues = useMemo(() => {
    return (incomingData: typeof issuesData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const assets = incomingData.pages.flatMap((page) => page.data);
      // A selected row key can index past the currently-loaded pages; drop the
      // resulting gaps so callers never dereference an undefined record.
      return ids
        .filter((id) => !isNaN(parseInt(id)))
        .map((id) => assets[parseInt(id)])
        .filter((issue) => issue !== undefined);
    };
  }, [selectedRows]);

  const { mutateAsync: bulkUpdateIssueState, isPending: isUpdatingIssueState } = useMutation({
    mutationFn: async ({ ids, state }: { ids: string[]; state: RecordState }) => await ApiService.BulkUpdateIssueState({ ids, state }),
    onSuccess: (_, { state }) => {
      const messages = {
        [RecordState.Active]: 'Issues restored successfully',
        [RecordState.Archived]: 'Issues archived successfully',
        [RecordState.Deleted]: 'Issues deleted successfully',
      };
      toast.success(messages[state]);
      setOpenArchiveConfirmation(false);
      setOpenRestoreConfirmation(false);
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: issueKeys.all });
      queryClient.invalidateQueries({ queryKey: repairKeys.all });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleArchiveIssues() {
    if (issuesData?.pages) {
      const toArchive = getSelectedIssues(issuesData);
      bulkUpdateIssueState({ ids: toArchive.map((issue) => issue.id), state: RecordState.Archived });
    }
  }

  function handleRestoreIssues() {
    if (issuesData?.pages) {
      const toRestore = getSelectedIssues(issuesData);
      bulkUpdateIssueState({ ids: toRestore.map((issue) => issue.id), state: RecordState.Active });
    }
  }

  function handleDeleteIssues() {
    if (issuesData?.pages) {
      const toDelete = getSelectedIssues(issuesData);
      bulkUpdateIssueState({ ids: toDelete.map((issue) => issue.id), state: RecordState.Deleted });
    }
  }

  const { mutateAsync: updateIssues, isPending: isUpdatingIssues } = useMutation({
    mutationFn: async ({ issueIds, issue }: UpdateIssuesRequestDTO) => await ApiService.UpdateIssues({ issueIds, issue }),
    onSuccess: () => {
      toast.success('Issues updated successfully');
      setOpenIssuesStatusUpdate(false);
      setOpenIssuesTagsUpdate(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: issueKeys.all });
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleUpdateIssuesStatus = async (newStatus: IssueStatus) => {
    if (!issuesData?.pages) return;
    const toUpdateStatus = getSelectedIssues(issuesData);
    await updateIssues({
      issueIds: toUpdateStatus.map((issue) => issue.id),
      issue: { status: newStatus as IssueStatus },
    });
    if (newStatus === IssueStatus.Closed && toUpdateStatus.length > 0) {
      // Attribute the prompt to the first selected issue — the rating is about the close-out experience, not a specific entity.
      triggerForIssue(toUpdateStatus[0]);
    }
  };

  const handleUpdateIssuesTags = async (newTags: string[], resetForm: () => void) => {
    if (issuesData?.pages) {
      const toUpdateStatus = getSelectedIssues(issuesData);
      await updateIssues({
        issueIds: toUpdateStatus.map((issue) => issue.id),
        issue: { tags: newTags },
      });
      resetForm();
    }
  };

  const selectedIssues = useMemo(() => getSelectedIssues(issuesData), [issuesData, getSelectedIssues]);

  const { shareUrl: shareTarget, appUrl } = getShareTargets('issues');

  function buildIssuesExportRequest(): RegisterExportQuery<'issues'> {
    const selected = getSelectedIssues(issuesData);
    return {
      queryType: 'issues',
      filters: { state: issueState, fields: { id: selected.map((issue) => issue.id) } },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetIssuesRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetIssuesRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      columns: activeExportColumnIds(columnOrder),
    };
  }

  function handleCsvExport() {
    if (!issuesData?.pages) return;

    openExportTab({ kind: 'csv', request: { ...buildIssuesExportRequest(), filename: buildCsvExportFilename('issues') } });
  }

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!issuesData?.pages) return;
    const selectedIssues = getSelectedIssues(issuesData);
    const overLimitError = overLimitExportError(scope, selectedIssues, {
      composeScope: PDF_SCOPE.REGISTER_ISSUES_AND_REPAIRS,
      parentNoun: 'issues',
      composeRecordNoun: 'issue and repair',
      linkedRecordId: (issue) => issue.repairId,
    });
    if (overLimitError) {
      toast.error(overLimitError);
      return;
    }
    openExportTab(buildPdfExportJob({ scope, recordType: 'issue', records: selectedIssues, register: buildIssuesExportRequest() }));
  }

  useEffect(() => {
    if (issuesError) {
      toast.error(issuesError.message);
    }
  }, [toast, issuesError]);

  useResetRowSelectionOnChange(setSelectedRows, [issueState, debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const canLoadMore = hasNextIssuesPage && !isFetchingIssues;

  useEffect(() => {
    if (loadMoreInView && canLoadMore) {
      // Default `cancelRefetch: true` would abort the in-flight invalidation refetch and re-append onto stale pages.
      fetchNextIssuesPage({ cancelRefetch: false });
    }
  }, [canLoadMore, fetchNextIssuesPage, loadMoreInView]);

  const flattenedData = useMemo(() => issuesData?.pages.flatMap((page) => page.data) ?? [], [issuesData]);

  function handleIssueDetailsCallback() {
    queryClient.invalidateQueries({ queryKey: issueKeys.all });
    queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
  }

  const handleRowClick = useCallback((issue: IssueDTO) => {
    setSelectedIssue(issue);
    setIsIssueDrawerOpen(true);
  }, []);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedIssues.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageTitle>Issues</TablePageTitle>
                  <ButtonStateToggle
                    options={[
                      { icon: <FolderOpenIcon />, label: 'Active', value: RecordState.Active },
                      { icon: <ArchiveIcon />, label: 'Archived', value: RecordState.Archived },
                    ]}
                    value={issueState}
                    onChange={setIssueState}
                    responsive
                  />
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.Issues, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex items-center">
                    <TablePageTableGroupDropdown
                      columns={groupOptions}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) =>
                        updatePageConfig({
                          table: { grouping: value ? [value] : [] },
                        })
                      }
                    />
                    <ColumnsDialog<IssueDTO>
                      open={openColumnsDialog}
                      defaultSectionLabel="Issue"
                      onOpenChange={setOpenColumnsDialog}
                      columns={createIssueTableColumns({ units })}
                      columnOrder={columnOrder}
                      onColumnOrderChange={(columnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: columnOrder as Array<{ id: IssueColumnId; active: boolean }> },
                        })
                      }
                      disabled={isLoadingPageConfig || isLoadingUnits}
                    />
                    {shareTarget && <TablePageHeaderActionButton onClick={() => setShareUrl(shareTarget)} icon={<ShareIcon />} label="Share Link" />}
                    {appUrl && <TablePageHeaderActionButton onClick={() => window.open(appUrl, '_blank')} icon={<LinkViewIcon />} label="Open Mechanic App" />}
                  </div>
                  <IssueDrawer source={IssueSource.Manual} />
                </div>
              </>
            )}

            {selectedIssues.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedIssues.length} totalCount={issuesData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadCsvIcon />} label="Download CSV" onClick={handleCsvExport} />}
                {canExportCsv && (
                  <DownloadPdfMenu
                    recordNoun="issue"
                    extraItems={[
                      {
                        scope: PDF_SCOPE.REGISTER_ISSUES_AND_REPAIRS,
                        label: 'Register, issue records and repair form records',
                        help: 'Download the summary sheet, then each selected issue immediately followed by its linked repair form, bound into one PDF.',
                      },
                    ]}
                    onSelect={handlePdfScopeSelect}
                  />
                )}
                {isAdmin && <TablePageHeaderActionButton icon={<GearIcon />} label="Edit Issue Status" onClick={() => setOpenIssuesStatusUpdate(true)} />}
                {isAdmin && <TablePageHeaderActionButton icon={<TagIcon />} label="Edit Tags" onClick={() => setOpenIssuesTagsUpdate(true)} />}
                {isAdmin && issueState === RecordState.Active && (
                  <TablePageHeaderActionButton icon={<ArchiveIcon />} label="Archive Selected" onClick={() => setOpenArchiveConfirmation(true)} />
                )}
                {isAdmin && issueState === RecordState.Archived && (
                  <>
                    <TablePageHeaderActionButton icon={<UndoIcon />} label="Restore Selected" onClick={() => setOpenRestoreConfirmation(true)} />
                    <TablePageHeaderActionButton
                      icon={<DeleteIcon className="text-default-red" />}
                      label="Delete Selected"
                      onClick={() => setOpenDeleteConfirmation(true)}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <IssuesTable
          data={flattenedData}
          loading={(isFetchingIssues && !isFetchingNextIssuesPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoIssueFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoIssueFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.Issues, [])} />
            ) : (
              <NoIssueAvailableDisplay />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.Issues, filters)}
          onRowClick={handleRowClick}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          units={units}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
        />
        {hasNextIssuesPage && (
          <Button
            ref={loadMoreRef}
            onClick={() => fetchNextIssuesPage({ cancelRefetch: false })}
            disabled={!canLoadMore}
            className="mx-auto w-full text-center"
            variant="ghost">
            {isFetchingNextIssuesPage ? 'Loading more...' : hasNextIssuesPage ? 'Load Newer' : 'You have reached the end.'}
          </Button>
        )}
      </ScrollArea>

      <ArchiveConfirmationDialog
        open={openArchiveConfirmation}
        onOpenChange={setOpenArchiveConfirmation}
        callback={handleArchiveIssues}
        loading={isUpdatingIssueState}
        title="Archive issues?"
        description="Are you sure you want to archive these issues? You can restore them later from the archived tab."
      />
      <RestoreConfirmationDialog
        open={openRestoreConfirmation}
        onOpenChange={setOpenRestoreConfirmation}
        callback={handleRestoreIssues}
        loading={isUpdatingIssueState}
        title="Restore issues?"
        description="Are you sure you want to restore these issues? They will be moved back to your active issues."
      />
      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteIssues}
        loading={isUpdatingIssueState}
        title="Delete issues?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these issues?"
      />

      <UpdateIssuesStatusDialog
        open={openIssuesStatusUpdate}
        onOpenChange={setOpenIssuesStatusUpdate}
        callback={handleUpdateIssuesStatus}
        loading={isUpdatingIssues}
      />

      <UpdateIssuesTagsDialog open={openIssuesTagsUpdate} onOpenChange={setOpenIssuesTagsUpdate} callback={handleUpdateIssuesTags} loading={isUpdatingIssues} />

      {shareUrl && <ShareDialog url={shareUrl} onOpenChange={() => setShareUrl(null)} open={!!shareUrl} />}

      <IssueDrawer
        noTrigger
        issueId={selectedIssue?.id ?? state?.issueId}
        asset={selectedIssue?.asset ?? undefined}
        open={isIssueDrawerOpen}
        onOpenChange={(open, success) => {
          setIsIssueDrawerOpen(open);
          if (!open && success) handleIssueDetailsCallback();
        }}
        callback={handleIssueDetailsCallback}
        source={(selectedIssue?.source as IssueSource) ?? IssueSource.Manual}
        checkFormElementId={selectedIssue?.checkFormElementId ?? undefined}
        locationId={selectedIssue?.locationId ?? undefined}
        onCloseSuccess={triggerForIssue}
      />

      <FeedbackPrompt {...feedbackPromptProps} />
    </div>
  );
};

export default IssueActivityPage;
